/**
 * The app's one shared notice channel.
 *
 * Before this, failures that were not tied to a page region used the browser's
 * `alert()` — modal, unstyled, and fired at whatever page the user had moved
 * on to. DOP-268 replaces those five call sites with an in-app toast.
 *
 * Deliberately minimal: no library, no positioning API, no promise-toasts.
 * The pre-existing ad-hoc banners (Workflows/WorkflowToast, ChatExportDropdown,
 * Sidebar) are NOT migrated here — that is follow-up work, not this ticket.
 */

import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import Toast, {
	ToastMessage,
	ToastPauseReason,
	ToastVariant,
} from "../components/Toast";

const AUTO_DISMISS_MS = 3000;

/** Beyond this the stack stops being a notice and starts being a wall. */
const MAX_VISIBLE = 3;

interface ToastContextValue {
	/** Show a notice. Returns nothing — fire and forget, like `alert()` was. */
	showToast: (text: string, variant?: ToastVariant) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

interface ToastProviderProps {
	children: React.ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
	const [toasts, setToasts] = useState<ToastMessage[]>([]);
	const nextIdRef = useRef(1);
	const timersRef = useRef<Map<number, number>>(new Map());
	// id -> the reasons currently holding it open. A single flag could not tell
	// "mouse left but keyboard is still here" from "nothing is holding this".
	const pausedRef = useRef<Map<number, Set<ToastPauseReason>>>(new Map());
	const containerRef = useRef<HTMLDivElement>(null);
	// Set only when a dismissal came from inside the stack, so an auto-dismiss
	// never yanks focus away from whatever the user is doing.
	const restoreFocusRef = useRef(false);

	const clearTimer = useCallback((id: number) => {
		const timer = timersRef.current.get(id);
		if (timer !== undefined) {
			window.clearTimeout(timer);
			timersRef.current.delete(id);
		}
	}, []);

	const dismiss = useCallback((id: number) => {
		setToasts((current) => current.filter((toast) => toast.id !== id));
	}, []);

	const handleDismiss = useCallback(
		(id: number) => {
			restoreFocusRef.current =
				containerRef.current?.contains(document.activeElement) ?? false;
			dismiss(id);
		},
		[dismiss],
	);

	/**
	 * Errors are never auto-dismissed: they took the place of a modal alert the
	 * user had to acknowledge, and a message that vanishes on its own is a
	 * message the user can miss entirely.
	 */
	const startTimer = useCallback(
		(toast: ToastMessage) => {
			if (toast.variant === "error") return;
			clearTimer(toast.id);
			timersRef.current.set(
				toast.id,
				window.setTimeout(() => dismiss(toast.id), AUTO_DISMISS_MS),
			);
		},
		[clearTimer, dismiss],
	);

	// Hover or focus holds a notice open — three seconds is a reading deadline,
	// and the pointer is already on the card because someone is reading it.
	const pause = useCallback(
		(id: number, reason: ToastPauseReason) => {
			const reasons = pausedRef.current.get(id) ?? new Set<ToastPauseReason>();
			reasons.add(reason);
			pausedRef.current.set(id, reasons);
			clearTimer(id);
		},
		[clearTimer],
	);

	const resume = useCallback(
		(id: number, reason: ToastPauseReason) => {
			const reasons = pausedRef.current.get(id);
			reasons?.delete(reason);
			// Still hovered, or still focused: the clock stays stopped.
			if (reasons && reasons.size > 0) return;
			pausedRef.current.delete(id);
			const toast = toasts.find((candidate) => candidate.id === id);
			if (toast) startTimer(toast);
		},
		[startTimer, toasts],
	);

	const showToast = useCallback(
		(text: string, variant: ToastVariant = "error") => {
			// The id is minted OUTSIDE the updater: React may invoke an updater
			// more than once (StrictMode does, every time), and an id that
			// increments in there drifts.
			const id = nextIdRef.current++;
			setToasts((current) => {
				// The same failure twice (two files, one broken export) is one
				// notice with a fresh clock, not two identical cards.
				const kept = current.filter(
					(toast) => !(toast.text === text && toast.variant === variant),
				);
				// Drop oldest first: beyond MAX_VISIBLE a stack stops being a
				// notice and starts being a wall.
				return [...kept, { id, text, variant }].slice(-MAX_VISIBLE);
			});
		},
		[],
	);

	// Timers follow the rendered stack rather than the call that created it:
	// whatever is on screen and unpaused gets a clock, whatever left loses one.
	useEffect(() => {
		const live = new Set(toasts.map((toast) => toast.id));
		timersRef.current.forEach((timer, id) => {
			if (!live.has(id)) {
				window.clearTimeout(timer);
				timersRef.current.delete(id);
			}
		});
		pausedRef.current.forEach((_reasons, id) => {
			if (!live.has(id)) pausedRef.current.delete(id);
		});
		toasts.forEach((toast) => {
			if (!timersRef.current.has(toast.id) && !pausedRef.current.has(toast.id))
				startTimer(toast);
		});
	}, [toasts, startTimer]);

	// Focus lived inside the card that just went away; hand it to the next
	// notice if there is one rather than letting it fall to the document.
	useEffect(() => {
		if (!restoreFocusRef.current) return;
		restoreFocusRef.current = false;
		containerRef.current?.querySelector("button")?.focus();
	}, [toasts]);

	useEffect(() => {
		const timers = timersRef.current;
		return () => {
			timers.forEach((timer) => window.clearTimeout(timer));
			timers.clear();
		};
	}, []);

	// Stable identity: this value crosses every consumer in the tree.
	const value = useMemo(() => ({ showToast }), [showToast]);

	return (
		<ToastContext.Provider value={value}>
			{children}
			{/* Top-right on purpose: UploadTray owns `fixed bottom-4 right-4 z-50`,
			    and a toast anchored there painted over its Retry button — the one
			    control the failure notice is telling the user to press. */}
			<div
				ref={containerRef}
				data-testid="toast-stack"
				className="pointer-events-none fixed top-4 right-4 z-[60] flex flex-col gap-2"
			>
				{toasts.map((toast) => (
					<Toast
						key={toast.id}
						toast={toast}
						onDismiss={handleDismiss}
						onPause={pause}
						onResume={resume}
					/>
				))}
			</div>
		</ToastContext.Provider>
	);
};

/**
 * Outside a provider (unit tests rendering a component in isolation) this
 * degrades to a console line rather than throwing — a missing notice must
 * never be the thing that breaks the surface it was reporting on.
 */
export function useToast(): ToastContextValue {
	const context = useContext(ToastContext);
	return useMemo<ToastContextValue>(
		() =>
			context ?? {
				showToast: (text: string) =>
					console.warn("[toast] no ToastProvider mounted:", text),
			},
		[context],
	);
}
